import React, { memo } from 'react';
import { Handle, Position } from 'reactflow';
import { Timer, CheckCircle2, AlertTriangle } from 'lucide-react';
import { ICON_PX } from '../../ui';

/**
 * Format milliseconds to a human-readable duration.
 */
function formatDuration(ms) {
  if (!ms) return '';
  const hours = Math.floor(ms / 3600000);
  const minutes = Math.floor((ms % 3600000) / 60000);
  if (hours >= 24) {
    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;
    return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`;
  }
  if (hours > 0) return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  return `${minutes}m`;
}

/**
 * Timer/SLA node — shows countdown, deadline, or duration.
 * Turns yellow when approaching deadline, red when breached.
 */
function TimerNode({ data, selected }) {
  const { label, status = 'pending', duration, slaWarning, slaBreach } = data;
  const isSatisfied = status === 'satisfied';
  const isAvailable = status === 'available';
  const isBreached = slaBreach;
  const isWarning = slaWarning && !slaBreach;

  return (
    <>
      <Handle type="target" position={Position.Top} className="!bg-[var(--slate)] !w-2.5 !h-2.5 !border-2 !border-white !rounded-full" />

      <div
        className={[
          'rounded-2xl border-2 px-4 py-2.5 min-w-[150px] text-center transition-all',
          isBreached ? 'bg-red-bg border-[var(--red-critical)] shadow-md' :
          isWarning ? 'bg-[var(--yellow)]/10 border-[var(--yellow)] shadow-md' :
          isSatisfied ? 'bg-green-bg border-[var(--green)] shadow-sm' :
          isAvailable ? 'bg-[var(--aqua)]/5 border-[var(--aqua)] shadow-md' :
          'bg-white border-slate-200 shadow-sm',
          selected ? 'ring-2 ring-aqua ring-offset-2' : '',
        ].join(' ')}
      >
        <div className="flex items-center justify-center gap-1.5 mb-0.5">
          {isBreached ? (
            <AlertTriangle size={ICON_PX.sm} className="text-[var(--red-critical)]" />
          ) : isSatisfied ? (
            <CheckCircle2 size={ICON_PX.sm} className="text-green-nn" />
          ) : (
            <Timer size={ICON_PX.sm} className={isWarning ? 'text-[var(--yellow)]' : isAvailable ? 'text-[var(--aqua)]' : 'text-slate-400'} />
          )}
          <span className={`text-xs font-semibold uppercase tracking-wide ${
            isBreached ? 'text-[var(--red-critical)]' :
            isWarning ? 'text-[var(--yellow)]' :
            isSatisfied ? 'text-green-nn' :
            isAvailable ? 'text-[var(--aqua)]' : 'text-slate-400'
          }`}>
            {isBreached ? 'SLA Breach' : isWarning ? 'SLA Warning' : 'Timer'}
          </span>
        </div>
        <span className={`text-sm font-medium ${
          isBreached ? 'text-[var(--red-critical)]' : isSatisfied ? 'text-green-nn' : 'text-text-dark'
        }`}>
          {label || (duration ? formatDuration(duration) : 'Timer')}
        </span>
        {duration && (
          <div className="text-[10px] text-slate-400 mt-0.5">{formatDuration(duration)}</div>
        )}
      </div>

      <Handle type="source" position={Position.Bottom} className="!bg-[var(--slate)] !w-2.5 !h-2.5 !border-2 !border-white !rounded-full" />
    </>
  );
}

export default memo(TimerNode);
